import { useTranslations } from '@vocab/react';
import {
  Box,
  Pagination as BraidPagination,
  useResponsiveValue,
} from 'braid-design-system';
import type { FC } from 'react';

import type { PaginationButtonType } from 'src/utils/events';

import translations from './.vocab';

interface Props {
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number, buttonType: PaginationButtonType) => void;
}

const Pagination: FC<Props> = ({
  currentPage,
  totalPages,
  handlePageChange,
}) => {
  const { t } = useTranslations(translations);

  // Once BRAID releases new version of the Paginaion component, we just need to pass the object as pageLimit till that time we can use useResponsiveValue
  const pageLimit =
    useResponsiveValue()({
      mobile: 3,
      tablet: 3,
      desktop: 4,
      wide: 7,
    }) ?? 4;

  let activePage = 1;

  if (totalPages <= 1) {
    return <></>;
  }

  activePage = currentPage > totalPages ? totalPages : currentPage;

  const getPageText = (page: number) => {
    if (page > currentPage) {
      return 'next';
    }
    if (page < currentPage) {
      return 'previous';
    }
    return 'number';
  };

  return (
    <Box
      background="customLight"
      paddingX="medium"
      paddingY="small"
      display="flex"
      justifyContent="center"
    >
      <BraidPagination
        page={activePage}
        total={totalPages}
        label={t('Page navigation')}
        pageLimit={pageLimit}
        nextLabel={t('Next')}
        previousLabel={t('Previous')}
        pageLabel={(page) =>
          t('Page {pageCount} of {totalPageCount}', {
            pageCount: page,
            totalPageCount: totalPages,
          })
        }
        linkProps={({ page: p }) => ({
          href: `#p-${p}`,
          onClick: (e) => {
            e.preventDefault();
            handlePageChange(p, getPageText(p));
          },
        })}
      />
    </Box>
  );
};

export default Pagination;
